"use client";

/* ─────────────────────────────────────────────────────────────────────────
 * Amour — a heart assembled out of photographs, with a hole in it.
 *
 * The section opens on BLACK. Nothing is on the stage and nothing is loading
 * in front of you; the photographs are already decoded and waiting below the
 * frame. Then:
 *
 *   1. RISE     the frames travel UP out of the dark and settle into a heart —
 *               a mosaic whose cells get smaller row by row, so the shape is
 *               coarse across the lobes and fine at the point. They land from
 *               the TOP DOWN, row by row, so the lobes arrive first and the
 *               point finishes the shape.
 *   2. VOID     the middle of the mosaic is missing. Not covered, not dimmed —
 *               the cells that would sit there are never dealt, and the few
 *               that straddle its edge are clipped against it, so the hole has
 *               a true rectangular edge rather than a mosaic's staircase.
 *   3. PIXELS   then the hole fills. Single squares fly out of the surrounding
 *               photographs and settle into a second, much smaller heart with
 *               the same outer silhouette as the big one. Most land as white
 *               paper; about one in four carries the photograph it left, so
 *               the little heart is visibly made out of the big one.
 *
 * And then it STOPS. Nothing loops, nothing breathes, nothing pulses. An
 * earlier build gave the finished heart a lub-dub and it was the wrong
 * instinct: the composition is fifty photographs somebody is meant to look
 * at, and a mosaic that swells forever makes every one of them resample on
 * every frame. The motion is the arrival. Once it has arrived, it holds.
 *
 * HOW IT IS DRAWN. One canvas, one requestAnimationFrame clock, fixed 1/60
 * substeps. GSAP owns nothing — the whole sequence is a pure function of one
 * elapsed-seconds scalar, which is what makes it re-drivable later by scroll
 * without rewriting a single beat.
 *
 * The one trick worth naming: there is no per-frame blur anywhere. A canvas
 * `filter: blur()` is a full-surface filter pass per draw and it will stall
 * the main thread outright. Softness during the rise is a 32-pixel-wide bake
 * of each photograph drawn up to cell size and cross-faded against the sharp
 * original — which is both free and a better match for how an out-of-focus
 * frame actually resolves.
 * ───────────────────────────────────────────────────────────────────────── */

import { useEffect, useRef, useState } from "react";
import { IMG_BASE, META, PHOTOS, STUDIO } from "./data";

/* ── the heart ───────────────────────────────────────────────────────────
 * ONE shape serves the whole section — the mosaic's silhouette and the pixel
 * heart inside it are the same function sampled at two resolutions, which is
 * why the small heart reads as a child of the big one rather than as a second
 * drawing that happens to be nearby.
 *
 * It is built the way a PIXEL heart is built, not the way a maths heart is:
 * two overlapping discs and a tapering skirt down to a point. The obvious
 * choice — the implicit curve (x²+y²−1)³ − x²y³ ≤ 0 — was tried first and
 * abandoned, because its cleft is far too shallow to survive being sampled on
 * a lattice: at any row height a mosaic can actually use, the notch has
 * already closed and the top row comes out as one solid bar. Discs give the
 * notch an explicit depth, and it is set here to about one row.
 *
 *   discs   r = 0.50, centred at (±0.62, 0.34)  → lobes top out at 0.84
 *   skirt   for y ≤ 0.34, |x| ≤ 1.12·((y+1)/1.34)^0.59  → a point at (0,−1)
 *
 * Spans x ∈ [−1.12, 1.12], y ∈ [−1.00, 0.84]. The discs do not meet on the
 * axis at all (0.62 > 0.50), so the notch runs clean down to where the skirt
 * takes over at y = 0.34 — which is what makes it survive being sampled.
 *
 * THE DISC SEPARATION IS THE WHOLE DESIGN, and it has to beat the CELL, not
 * merely exist. A notch is a V, so it is narrowest exactly where the top row
 * samples it, and the top row's cells are the biggest in the mosaic — so this
 * is the worst case in the whole layout, and it is the one that decides
 * whether the heart reads as a heart or as a bag.
 *
 * The margin was measured rather than guessed, on both solves:
 *
 *              notch half-width   cell half-step   result
 *   0.54  wide       0.168            0.153        gap  (by 0.015)
 *   0.54  tall       0.156            0.166        BAR  (missed by 0.010)
 *   0.62  wide       0.210            0.121        gap  (by 0.089)
 *   0.62  tall       0.210            0.121        gap  (by 0.089)
 *
 * At 0.54 the desktop solve cleared it by a hundredth and the phone solve
 * failed by a hundredth — the cleft was effectively decided by rounding. At
 * 0.62 both clear it comfortably, ONE cell pair is dropped from the top row,
 * and the second row still comes out solid (its notch is zero — by row 2 the
 * shape has dropped below the skirt line, where the two lobes have already
 * merged). One row deep, two cells wide, and the top row keeps two whole
 * photographs on each lobe rather than one. */
const HX = 1.12;
const HY_TOP = 0.84;
const HY_BOT = -1.0;
const HW = HX * 2;
const HH = HY_TOP - HY_BOT;

const D_R2 = 0.5 * 0.5;
const D_CX = 0.62;
const D_CY = 0.34;

/** How far the heart reaches from the axis at height `y` — the OUTER
 *  silhouette only, with no cleft in it. */
function heartOuterAt(y: number) {
  const dy = y - D_CY;
  const disc = Math.abs(dy) < 0.5 ? D_CX + Math.sqrt(D_R2 - dy * dy) : 0;
  if (dy > 0) return disc;
  /* The skirt. The exponent is what decides whether the mosaic tapers or just
   * stops: at 0.72 the shape barely narrowed over the two rows below the
   * lobes, so those rows — being made of smaller cells — fitted MORE of them
   * and came out as wide as the widest row. The heart grew a shelf. Solved
   * against the reference's own row widths, 0.59 tracks it to within 0.015. */
  const skirt = HX * Math.pow((y + 1) / (1 + D_CY), 0.59);
  return Math.max(disc, skirt);
}

/** The cleft between the lobes, as a half-width at height `y`. This is the
 *  gap the discs leave: 0.12 where they pass the lobe centres, widening to
 *  D_CX at the very top. Below the lobe centres the skirt has closed it. */
function cleftAt(y: number) {
  const dy = y - D_CY;
  if (dy <= 0 || dy >= 0.5) return 0;
  return Math.max(0, D_CX - Math.sqrt(D_R2 - dy * dy));
}

/** THE SMALL HEART IS DRAWN, NOT SAMPLED.
 *
 *  It used to be the mosaic's own curve evaluated on a 9-wide grid, with a
 *  separate shallower cleft to survive the coarser sampling. That was the
 *  wrong tool twice over. Sampling a smooth curve at nine columns puts the
 *  boundary wherever the sample points happen to fall, so the two sides came
 *  out unequal — one lobe a cell wider than the other, the point off the
 *  axis — and no amount of tuning the curve fixes it, because the asymmetry
 *  is in the sampling rather than in the shape.
 *
 *  A pixel heart is a piece of pixel art. It gets authored, cell by cell, and
 *  then it is symmetric because it was WRITTEN symmetric — every row here is a
 *  palindrome, which is a property you can check by eye in the source. This is
 *  the classic 11x9 heart: two three-wide lobes with a two-wide notch, three
 *  full rows through the middle, then a clean diagonal taper to a single cell
 *  at the point. */
const PIX_HEART = [
  ".###...###.",
  "###########",
  "###########",
  "###########",
  ".#########.",
  "..#######..",
  "...#####...",
  "....###....",
  ".....#.....",
];
const PIX_COLS = PIX_HEART[0].length;
const PIX_ROWS = PIX_HEART.length;

/* ── the tiles keep turning over ───────────────────────────────────────────
 * Every cell of the small heart cycles: paper, then a photograph fading up,
 * held, faded away, then paper again. Each cell runs on its own period and
 * its own phase, so at any moment a third of them are carrying a picture and
 * the set is never the same twice. It is the only thing on the settled stage
 * that moves, and it is what stops the heart reading as a printed logo. */
const TILE_IN = 0.55; // a photograph fades up over this
const TILE_HOLD = 1.5; // and stays
const TILE_OUT = 0.6; // then leaves
const TILE_MIN = 3.4; // shortest full cycle, blank time included
const TILE_MAX = 6.8;

function insideHeart(x: number, y: number) {
  const ax = Math.abs(x);
  return ax <= heartOuterAt(y) && ax >= cleftAt(y);
}


/* ── the beats, in seconds ───────────────────────────────────────────────
 * Every one of these is read off a single elapsed-seconds scalar. Nothing is
 * a tween and nothing is stateful, so the sequence can be scrubbed, replayed,
 * or handed to a scroll position later without touching anything below. */
const T_BLACK = 0.34; // the stage is empty this long, deliberately
const T_RISE = 1.18; // how long ONE frame takes to travel and settle
const RISE_SPREAD = 1.15; // first row to last, across the whole mosaic
/** How much of ONE row-step the left-to-right sweep inside a row may use.
 *  Below 1 by construction: at 1 the last frame of a row would land exactly as
 *  the next row starts, and anything above it puts two rows in the air at once,
 *  which is the disorder this is here to prevent. */
const IN_ROW_SWEEP = 0.55;
const T_PIX_IN = 2.05; // the first pixel leaves its photograph
const T_PIX = 0.92; // how long ONE pixel takes to cross and land
const PIX_SPREAD = 0.72;
/** The heart is whole here. */
const T_WHOLE = T_PIX_IN + PIX_SPREAD + T_PIX + 0.4;
/* ── the spread ──────────────────────────────────────────────────────────
 * The heart holds for a beat, then GROWS — every photograph travels outward
 * from the centre until the mosaic has burst its own frame and the frames are
 * scattered across the whole section. The growth and the scatter are the same
 * motion: each cell is simply interpolated from its slot in the heart to its
 * slot in the field, and because the field is bigger than the heart in every
 * direction the interpolation reads as expansion rather than as a reshuffle.
 *
 * The pixel heart grows with it — much faster, and fading as it goes, so the
 * thing you built blooms past the camera and leaves the photographs behind. */
/** The pixel heart's FINAL WIDTH, as a fraction of the viewport's width. It
 *  does NOT leave — it grows to this and stays, centred, with the photographs
 *  spread around it.
 *
 *  A fraction of the viewport rather than a multiple of its starting size,
 *  because those are different questions. As a raw multiplier (it was 7x) the
 *  answer depended on how big the hole in the mosaic happened to be, which is
 *  itself derived from the row profile — so tuning the mosaic silently
 *  resized the heart, and on a desktop it grew to well over half the screen.
 *  Expressed this way the final size is stated outright and the growth is
 *  whatever gets it there.
 *
 *  A phone gets a slightly larger share: the same fraction of a 390px screen
 *  is barely fifty pixels, and the heart has to survive being made of nine
 *  squares across. */
const HEART_FINAL_W = 0.098;
const HEART_FINAL_W_TALL = 0.165;
/** How far BELOW true centre the heart settles, as a fraction of its own
 *  height. Measured, the heart is already centred to the pixel — bounding box
 *  dead on, centre of area two pixels high — so this is not a geometry fix; it
 *  is an optical one. A heart carries its mass in the wide upper lobes and
 *  tapers to a point, and a shape like that reads high when it is placed at
 *  true centre. Expressed against the heart's own height rather than the
 *  viewport's so the correction scales with the object it is correcting. */
const HEART_DROP = 0.18;
const T_HOLD = 0.75; // the finished heart is left alone this long
const T_SPREAD_IN = T_WHOLE + T_HOLD;
const T_SPREAD = 2.1; // one cell's journey out
const SPREAD_STAGGER = 0.8; // centre leaves first, the point last
/** Everything is over by here — the field is settled and clickable. */
const T_END = T_SPREAD_IN + T_SPREAD + SPREAD_STAGGER;

/* ── how much room the heart gets ────────────────────────────────────────
 * The split headline either FLANKS the heart or stacks UNDER it, and the
 * heart's height depends on which — flanked it has the whole frame, stacked it
 * has the band above the copy. ONE fraction serves both, taken of whatever
 * band the copy leaves, rather than a per-orientation number: the old pair
 * (0.66 of the viewport wide, 0.46 of it tall) were the same fraction of their
 * own band all along, written twice and free to drift apart. */
const HEART_BAND = 0.655;
/** Where the stacked copy starts, as a fraction of the height. The canvas
 *  hands this to the stylesheet as a pixel value, so the type and the shape
 *  cannot disagree about where the band ends. */
const COPY_TOP = 0.72;

/* ── the frames near the cursor lean toward it ───────────────────────────
 * Move the pointer and the photographs AROUND it drift toward it, the nearest
 * furthest, the rest of the wall dead still.
 *
 * It was the whole room shifting at once — a camera-pan parallax, every frame
 * moving by its depth. That reads as the window swinging rather than as
 * touching anything, and on a wall of forty-seven photographs it is a lot of
 * motion to answer a hand that only ever points at one of them. A local pull
 * says something the pan cannot: THIS one, and the few beside it.
 *
 * The frame directly under the cursor barely moves, and that falls out of the
 * maths rather than being a rule — the pull points AT the cursor, so a frame
 * the cursor is already on has nowhere to go. What you see is the ring around
 * the pointer leaning in, which is exactly the gesture of gathering something
 * toward your hand.
 *
 * NOTHING MOVES WHILE THE HEART IS WHOLE. The pull fades in with `se`, the
 * same scalar the other depth cues ride, so it belongs to the FIELD only. A
 * mosaic is a composition and cells pulled out of it are just a broken
 * picture; the heart holds still and the wall answers you. */
/** How far the pull reaches, as a fraction of the short side — about two
 *  frames, so the answer is local without being a single-frame effect. */
const PULL_R = 0.3;
/** The most a frame may travel, as a fraction of THE GUTTER — the minimum
 *  black the field solve leaves between neighbours (§the field). Expressed
 *  against the gutter rather than the viewport because it is the gutter this
 *  has to stay inside: two frames either side of the cursor both lean toward
 *  it, so they close on each other by twice this, and at 0.45 they still
 *  cannot meet. The wall's no-overlap guarantee is structural and this does
 *  not get to be the thing that breaks it. */
const PULL_CAP = 0.45;
/** A near frame leans a little more than a far one — the same reading as the
 *  tilt: at the back of the room, less of everything. */
const PULL_FAR = 0.6;

/* ── palette ─────────────────────────────────────────────────────────────
 * The section is BLACK in both themes. It is not theme-agnostic by accident:
 * the photographs are the only colour on the stage, and a bone-coloured
 * version of this would put fifty saturated frames on a light ground and lose
 * the one thing the composition is doing. */
const STAGE = "#050506";
const INK = "#F4F1EA";

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const clamp = (v: number, lo: number, hi: number) =>
  v < lo ? lo : v > hi ? hi : v;
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
/** power4.out — the entrance curve the rest of the library uses. */
const out4 = (k: number) => 1 - Math.pow(1 - k, 4);
const out2 = (k: number) => 1 - Math.pow(1 - k, 2);
/** Smootherstep — zero first AND second derivative at both ends, which is as
 *  gentle as a 0→1 ramp gets. Used for the spread, where the frames are
 *  leaving a dead stop: `power4.out` starts at full speed, so the wall LURCHED
 *  out of the heart, and no amount of lengthening the duration fixed a curve
 *  whose problem was the first frame. This one eases out of rest and into
 *  rest. */
const smoother = (k: number) => k * k * k * (k * (k * 6 - 15) + 10);

function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ── layout ─────────────────────────────────────────────────────────────── */
interface Cell {
  x: number;
  y: number;
  w: number;
  h: number;
  row: number;
  /** index into PHOTOS */
  p: number;
  /** seconds before this frame starts travelling */
  delay: number;
  /** how far below its home it starts */
  drop: number;
  /** per-cell rotation at launch, radians */
  spin: number;
  /** Where this frame ends up once the heart has burst — its slot in the
   *  scattered field. Centre, not corner, because the cell grows about its
   *  own middle on the way out. */
  fx: number;
  fy: number;
  fw: number;
  fh: number;
  /** seconds before it leaves the heart */
  fdelay: number;
  /** a few degrees of lean in the field — a wall of pictures is never square */
  ftilt: number;
  /** paint order in the field; higher sits on top */
  fz: number;
  /** WHERE THIS FRAME SITS IN Z — 0 is far, 1 is near. One number drives
   *  everything that says depth: how big it is printed, how sharp it is, how
   *  much of the room's dark sits in front of it, and what paints over what.
   *  Scale alone reads as "assorted sizes"; scale plus the other three reads
   *  as distance. */
  fdepth: number;
}

interface Pix {
  x: number;
  y: number;
  s: number;
  delay: number;
  /** where it leaves from — the centre of a real photograph in the mosaic */
  fx: number;
  fy: number;
  /** WHICH photograph it left, as an index. Deliberately not a colour: the
   *  layout is solved before a single frame has decoded, so a colour captured
   *  here is always the fallback and the pixel heart comes out uniformly pale.
   *  The photograph is looked up at paint time, once it exists. */
  p: number;
  /** seconds for one full show-and-hide cycle */
  cyc: number;
  /** where in that cycle this tile starts, 0–1 */
  phase: number;
  /** picks which photograph each cycle lands on */
  seed: number;
}

interface Layout {
  w: number;
  h: number;
  mode: "wide" | "tall";
  /** the minimum black between two frames in the field — the pull is budgeted
   *  against it, so the no-overlap guarantee survives the pointer */
  gut: number;
  /** whether the copy fits beside the heart — see the solve */
  flank: boolean;
  /** the top of the stacked copy in page pixels, handed to the stylesheet */
  copyTop: number;
  cx: number;
  top: number;
  hw: number;
  hh: number;
  cells: Cell[];
  /** the blank box */
  vx: number;
  vy: number;
  vw: number;
  vh: number;
  pix: Pix[];
  /** The pixel heart's FINAL footprint, centred in the section — what the
   *  field has to stay out of — and the scale that gets it there. */
  kx: number;
  ky: number;
  kw: number;
  kh: number;
  kscale: number;
  /** The detail card's rect, in page pixels. Solved HERE rather than in the
   *  stylesheet because the canvas has to fly the chosen photograph to exactly
   *  this box — two independent definitions would drift and the handover from
   *  the travelling frame to the card would visibly jump. The CSS reads these
   *  back as custom properties. */
  cdx: number;
  cdy: number;
  cdw: number;
  cdh: number;
}

export default function Amour({ className }: { className?: string }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const cvRef = useRef<HTMLCanvasElement | null>(null);
  const [ready, setReady] = useState(false);
  /** Which photograph the detail card is showing, if any. Index into PHOTOS. */
  const [ui, setUi] = useState<{ open: boolean; photo: number }>({
    open: false,
    photo: -1,
  });
  const apiRef = useRef<{
    close: () => void;
    step: (d: number) => void;
    open: (i: number) => void;
  } | null>(null);

  useEffect(() => {
    if (!rootRef.current || !cvRef.current) return;
    const root: HTMLDivElement = rootRef.current;
    const cv: HTMLCanvasElement = cvRef.current;
    const ctx0 = cv.getContext("2d", { alpha: false });
    if (!ctx0) return;
    const ctx: CanvasRenderingContext2D = ctx0;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let L: Layout | null = null;
    let dpr = Math.min(2, window.devicePixelRatio || 1);
    let paused = false;
    let raf = 0;

    /* ── the wall, once it is a wall ──────────────────────────────────────
     * `hover` and `sel` are INDICES INTO L.cells, not into PHOTOS — two cells
     * can carry the same photograph once the roll is shorter than the mosaic,
     * and pointing at one of them must not light the other. `hoverK` and
     * `openK` are the eased scalars the paint reads, so the dimming and the
     * lift never jump. */
    let hover = -1;
    let hoverK = 0;
    /* `sel` is the OPEN card and nothing else. It is cleared the instant the
     * card is dismissed, so the wall is clickable again immediately.
     *
     * The frame still has to fly home, which is what `retIdx`/`retK` are for: a
     * closing frame is RETIRED into its own slot and finishes the journey
     * there, on its own scalar. One scalar used to do both jobs, and it cost a
     * full second of dead wall — `sel` stayed set for the whole return trip so
     * that the painter had something to draw, and every guard written as
     * "sel >= 0" locked out clicks and hover along with it. Splitting them
     * separates the two questions that were being conflated: IS A CARD OPEN
     * (sel), and IS A PHOTOGRAPH STILL MOVING (retIdx). Only the first should
     * ever stop you pointing at something. A third flag for "on its way in or
     * out" went with it — with the return in its own slot, `sel >= 0` IS open,
     * and a second field that can never disagree is a field that will.
     *
     * One return slot, deliberately: open A, dismiss it, then open and dismiss
     * B before A has landed, and A snaps the last of the way home. It is a
     * tenth of a second of travel at that point, and the alternative is a list
     * of in-flight frames to keep the last few pixels of one. */
    let sel = -1;
    let openK = 0;
    let retIdx = -1;
    let retK = 0;

    /* The pointer in PAGE PIXELS — the pull is about distance to a frame, so a
     * normalised −1…1 vector is no longer the useful form — plus the eased
     * copy the paint reads, and how present the pointer is at all.
     *
     * `pullK` is why the presence is a scalar and not a boolean: leaving has
     * to release the frames over the same beat that entering gathers them, or
     * a wall of photographs snaps flat the moment the cursor crosses the edge.
     * It also stops the first move of all: `pullE` is snapped to the pointer
     * on arrival, so the field does not get dragged across the section from
     * wherever the cursor happened to be last. */
    const pullE = { x: 0, y: 0 };
    let pullK = 0;
    let pullIn = 0;

    /* ── photographs ──────────────────────────────────────────────────────
     * TWO things are kept per frame: the decoded image, and a 32-pixel bake
     * used as the "out of focus" state during the rise. Both are computed
     * once, on decode.
     *
     * NOTHING HERE READS PIXELS BACK. There is no getImageData anywhere in the
     * section, so the canvas is never tainted and the photographs can be served
     * from any host — a CDN, another bucket, a different origin entirely —
     * without a CORS grant and without `crossOrigin` on the Image.
     *
     * That is worth protecting. There used to be a third thing per frame: a
     * single averaged colour, meant to be what the pixel heart was built out
     * of. The heart ended up drawing paper and the bake instead, which reads
     * far better — and the average went on being computed, stored, and never
     * looked at, quietly holding the whole section hostage to a CORS header it
     * did not need. Reintroduce a pixel read for any reason and you inherit
     * that constraint. */
    const imgs = new Map<string, HTMLImageElement>();
    const soft = new Map<string, HTMLCanvasElement>();
    let decoded = 0;

    function load(src: string) {
      const im = new Image();
      im.decoding = "async";
      im.onload = () => {
        decoded++;
        imgs.set(src, im);
        /* the soft bake — 32px wide is enough to read as a defocused frame
         * and small enough that fifty of them cost nothing */
        const sc = document.createElement("canvas");
        sc.width = 32;
        sc.height = 40;
        const sg = sc.getContext("2d");
        if (sg) {
          sg.drawImage(im, 0, 0, 32, 40);
          soft.set(src, sc);
        }
      };
      im.onerror = () => decoded++;
      im.src = IMG_BASE + src;
      imgs.set(src, im);
    }
    for (const p of PHOTOS) load(p);

    /* ── the solve ────────────────────────────────────────────────────────
     * Rows are built from the TOP DOWN with a cell size that shrinks by a
     * constant ratio each row, which is the whole reason the mosaic reads as
     * a heart rather than as a heart-shaped crop of a grid: the point of the
     * heart needs finer cells than its lobes to hold an edge at all. */
    /* THE MARGIN HAS TO BE MEASURED, NOT ASSUMED.
     *
     * `solveAt` sizes the heart from the viewport and clamps `hw`, the width
     * the heart's COORDINATE SPACE maps onto. But the mosaic is drawn wider
     * than that space: a cell sits on its centre and reaches half a cell
     * further, and the widest row's outer bound is already within a per cent
     * of the space's own edge. On a phone that overshoot ate the margin
     * whole — the clamp asked for 90% of the viewport and the mosaic drew
     * 94%, close enough to the edges to read as clipped.
     *
     * So: solve, MEASURE what was actually laid down, and if it is over the
     * allowance, shrink by exactly the ratio and solve again. Two passes are
     * enough in practice; the third is a backstop. Deriving the overshoot in
     * closed form would mean predicting which cells survive the cleft and the
     * void box before laying them out, which is the layout itself. */
    function solve(w: number, h: number): Layout {
      const mode: "wide" | "tall" = w / h < 0.9 ? "tall" : "wide";
      const allowed = w * (mode === "tall" ? 0.9 : 0.94);
      let fit = 1;
      let out = solveAt(w, h, fit);
      for (let pass = 0; pass < 3; pass++) {
        if (!out.cells.length) break;
        let lo = Infinity;
        let hi = -Infinity;
        for (const c of out.cells) {
          if (c.x < lo) lo = c.x;
          if (c.x + c.w > hi) hi = c.x + c.w;
        }
        const extent = hi - lo;
        if (extent <= allowed) break;
        fit *= (allowed / extent) * 0.998;
        out = solveAt(w, h, fit);
      }
      return out;
    }

    function solveAt(w: number, h: number, fit: number): Layout {
      const mode: "wide" | "tall" = w / h < 0.9 ? "tall" : "wide";

      /* The heart is sized from the HEIGHT and then clamped to the width, and
       * when the clamp bites the height is re-derived from it rather than left
       * alone — a heart is a fixed proportion, and letting the width clip
       * while the height stays put does not crop it, it squashes it. On a
       * phone the clamp is always what wins. */
      /* ── does the copy fit beside the heart? ──────────────────────────
       * This is NOT a question about the viewport's shape, which is why no
       * media query can answer it: the thing that would have to be measured is
       * the heart, and the heart's height is what the answer decides. So it is
       * settled by drawing it — solve the heart as though the copy flanks it,
       * measure the column left at the sides, and stack the copy if a line of
       * type will not fit there.
       *
       * Between about 0.9 and 1.3 the aspect test and the real answer diverge
       * sharply, and the aspect test is the one that is wrong: at 1000x900 it
       * says "wide", while the heart comes out 630 across and leaves 185 a side
       * for a headline that needs 229. That is the case this replaces.
       *
       * `headEm` mirrors the stylesheet's own clamp on the payoff line, and the
       * column is about three and a bit of those plus the page margin. Mirrored
       * rather than measured off the DOM because the font-size the measurement
       * would return depends on the layout being chosen here. */
      const headEm = clamp(Math.min(w * 0.056, h * 0.1), 38, 88);
      const headCol = headEm * 3.2 + w * 0.026 + 24;

      const fitBand = (band: number) => {
        let bh = band * HEART_BAND * fit;
        let bw = bh * 1.06;
        const cap = w * (mode === "tall" ? 0.9 : 0.94) * fit;
        if (bw > cap) {
          bw = cap;
          bh = bw / 1.06;
        }
        return { bh, bw };
      };

      let band = h;
      let { bh: hh, bw: hw } = fitBand(band);
      const flank = (w - hw) / 2 >= headCol;
      if (!flank) {
        band = h * COPY_TOP;
        ({ bh: hh, bw: hw } = fitBand(band));
      }
      const cx = w * 0.5;
      /* CENTRED IN ITS BAND, and derived rather than dialled in. The mosaic's
       * box is exactly [top, top + hh] — the rows fill it with no slack — so
       * centring the box centres the heart. It was two hand-set fractions of
       * the viewport before (0.1 and 0.17), which meant the heart sat high with
       * a bay of dead black underneath it, and worse, the two numbers drifted
       * out of true the moment `hh` was tuned or the width clamp bit: a
       * fraction of the viewport cannot know how tall the heart it is placing
       * turned out to be. Written this way the placement follows the size.
       *
       * The band is the whole frame when the copy flanks the heart, and the
       * space above the copy when it stacks under — so on a phone the heart is
       * centred in the room it actually has rather than in a viewport whose
       * bottom third belongs to the type. Centring it in the frame there put
       * the point of the heart through the middle of the headline.
       *
       * NO OPTICAL DROP HERE, unlike the pixel heart's HEART_DROP. The same
       * argument applies — a shape with its mass in the lobes reads high at
       * true centre — but the mosaic is not floating in an empty frame the way
       * the small heart is: it hangs under a header bar and over the note, and
       * against that pair the geometric centre is what looks level. */
      const top = (band - hh) / 2;

      /* ── the row profile ──────────────────────────────────────────────
       * Cell size per row, relative to the top row. This was a constant ratio
       * (each row 0.9 of the one above), which distributes the shrink evenly
       * and gives every row the same faint difference from its neighbour —
       * legible as a taper, but not as a HIERARCHY.
       *
       * Written out instead, the mosaic has three registers rather than nine:
       * the top TWO rows are the big frames and share one size, so the lobes
       * read as a single band of hero images; rows three and four step down
       * together; and everything below drops away fast into the point. The
       * pairing is the point — two rows at one size say "these are the
       * subject" in a way that nine descending sizes never can. */
      /* These numbers are MEASURED, not invented. The row heights were taken
       * off the reference and the tail was then scaled, and the skirt exponent
       * solved with it, against the reference's own row half-widths — the fit
       * lands within 0.015 (mean, as a fraction of the heart's half-width)
       * over the rows it was taken from, which is closer than the measurement
       * itself is.
       *
       * THE NINTH ROW IS THE POINT, and it is why rows five to eight are not
       * the measured numbers any more. Cell counts are set by the ratio of a
       * row's height to the WHOLE profile's sum, so appending a row shrinks
       * every row above it and hands each one more cells: dropping a ninth row
       * onto the measured profile turned rows five, six and seven from six
       * photographs into eight and the row above the point from four into six.
       * The tail is re-proportioned to absorb it — the counts, not the
       * millimetres, are what the composition is — and rows one to eight come
       * out holding exactly what they held before.
       *
       * ONE profile serves both solves. Two — one per orientation — was the
       * earlier shape and it meant the phone quietly had a different heart. */
      const PROFILE = [1, 1, 0.655, 0.575, 0.52, 0.41, 0.32, 0.3, 0.17];
      const ROWS = PROFILE.length;

      /* PER-ROW PARITY — 1 = odd (one cell sits on the axis), 0 = even (cells
       * straddle it). This is an editorial table, not a formula, because the
       * cell COUNT of each row is a composition decision and parity is the
       * only thing that sets it: a row's capacity is fixed by the silhouette,
       * so the single remaining choice is whether that capacity is an odd or
       * an even number.
       *
       *   row 1  odd   5 slots, the cleft takes the middle  →  4 photographs
       *   row 2  even  ..........................................  6
       *   row 3  odd   7 slots, the HOLE takes the middle   →  3 + heart + 3
       *   row 4  odd   ..........................................  7
       *   rows 5-8 even .......................................  6, 6, 6, 4
       *   row 9  even  the point, capped below           →       2
       *
       * Rows 3 and 4 are odd for the same underlying reason row 1 is: only an
       * odd row has a centre cell, and only a centre cell can be removed on
       * its own. Row 3 needs that to seat the heart between three photographs
       * on each side; row 4 needs it simply to reach seven. */
      const PARITY = [1, 0, 1, 1, 0, 0, 0, 0, 0];

      /* THE POINT IS CAPPED, and this is an editorial number in exactly the
       * way PARITY is. The silhouette is still wide enough at the last row to
       * seat FOUR cells, and four cells at the tip is not a point — it is a
       * stub the same width as the row above it. The shape decides what CAN be
       * dealt; the composition decides what is. Only the two nearest the axis
       * are kept, so the mosaic ends the way the drawn heart does.
       *
       * It cannot be done with the profile instead: a last row the silhouette
       * only fits two cells into has to be about a fifteenth of the heart's
       * height, and a row that tall drags every count above it up with it —
       * the lobes go to six and eight photographs and the heart stops being
       * two hero rows over a taper. Searched exhaustively; there is no profile
       * that ends 4-then-2 and keeps this body. */
      const POINT_CELLS = 2;
      const AR = 1.16; // cells are portraits, like the frames in them
      const GAP = Math.max(1.5, hh * 0.006);

      /* solve the top row's height so the rows exactly fill the heart */
      const sum = PROFILE.reduce((a, v) => a + v, 0);
      const ch0 = hh / sum;

      /* The row bands are solved FIRST, because the blank box is snapped to
       * them. A box placed at an arbitrary height slices the rows it crosses
       * into horizontal slivers of photograph — a millimetre of somebody's
       * face along the top edge of the hole — which is the single ugliest
       * thing this composition can do. Snapped, its top and bottom edges ARE
       * row edges and no cell is ever cut horizontally. */
      const bands: { y0: number; y1: number; ch: number; cw: number }[] = [];
      let yCur = top;
      for (let r = 0; r < ROWS; r++) {
        const ch = ch0 * PROFILE[r];
        bands.push({ y0: yCur, y1: yCur + ch, ch, cw: ch / AR });
        yCur += ch;
      }

      /* The hole is EXACTLY ONE CELL of the third row — named outright rather
       * than derived from a fraction of the width, and sized to the cell
       * rather than to the heart.
       *
       * Both of those are load-bearing. Named, because with the profile
       * written by hand the rows are not evenly spaced, so "34% of the way
       * down" lands on a different row for every viewport shape, and a hole in
       * the middle of a picture has to be in the same place every time.
       *
       * Sized to the cell, because anything wider does not remove a second
       * photograph — it CLIPS one. A box a cell and a half across leaves the
       * neighbours with two-thirds of a face each, which reads as a rendering
       * fault rather than as a gap. At exactly one cell the centre photograph
       * is removed whole and the six around it are untouched. */
      const ra = Math.min(2, ROWS - 1);
      const vy = bands[ra].y0;
      const vh = bands[ra].ch;
      const vw = bands[ra].cw;
      const vx = cx - vw / 2;

      /* ── the pixel heart's size, solved HERE ──────────────────────────
       * up front, because the field below has to keep clear of where this
       * ends up. Fit the heart to its OWN proportion inside the hole and then
       * centre it — never stretch it to the hole. The hole is snapped to whole
       * mosaic rows, so its aspect is whatever those rows happen to add up to;
       * mapping the curve across that box directly made the small heart taller
       * and narrower than the big one, which quietly breaks the one claim the
       * composition makes — that these are the same shape at two resolutions.
       *
       * Pixels across is a LEGIBILITY budget, not a resolution: the hole is one
       * cell of the third row, and at thirteen across each square came out
       * about five pixels wide and the photograph tiles stopped being
       * photographs — they read as coloured dots. Nine is coarse enough that a
       * tile is recognisably an image and still fine enough to draw a heart. */
      /* The bitmap's own proportion, fitted inside the hole and centred —
       * never stretched to it. The hole is snapped to whole mosaic rows, so
       * its aspect is whatever those rows add up to; mapping the heart across
       * that box directly made the small heart taller and narrower than the
       * big one. */
      const inset = 0.9;
      const aspect = PIX_COLS / PIX_ROWS;
      let pixW = vw * inset;
      let pixH = pixW / aspect;
      if (pixH > vh * inset) {
        pixH = vh * inset;
        pixW = pixH * aspect;
      }

      const cells: Cell[] = [];
      const rnd = rng(20260817);

      for (let r = 0; r < ROWS; r++) {
        const { y0, ch, cw } = bands[r];
        const inVoidRows = r === ra;
        /* where this row's cells start, so the point can be trimmed once the
         * row is complete — the trim needs the whole row to choose from */
        const rowStart = cells.length;
        const yc = y0 + ch / 2;
        /* heart-space y at this row's centre line */
        const hy = HY_TOP - ((yc - top) / hh) * HH;
        /* PARITY DECIDES THE WIDTH OF THE GAP IN THE TOP ROW, and there is no
         * other lever that can.
         *
         * Walk a row in cell steps centred on the axis. With an EVEN count the
         * cells straddle the axis — none sits on it — so the cleft can only
         * ever remove a symmetric PAIR, and the gap in the top row is two
         * photographs wide no matter how narrow the cleft is made. With an ODD
         * count one cell sits dead on the axis, and that single cell is the one
         * the cleft takes: a gap exactly ONE photograph wide.
         *
         * Which rows are odd is the PARITY table above; the reasoning for
         * each is there. The counts cannot all come from one grid — four-with-
         * a-single-gap needs five slots, six needs six, seven needs seven —
         * so consecutive rows are deliberately offset by half a cell where
         * their parities differ. That is not a flaw to be tidied: it is what
         * lets the second row reach half a cell further out on each side than
         * the first, which is exactly the way a heart widens below its lobes.
         *
         * Left to fall where it may, the parity varies with the viewport and
         * the cleft and the hole appear and disappear by accident; pinning it
         * per row is what makes the heart the same on every screen. */
        const wantOdd = PARITY[r] === 1;
        let n = Math.ceil(hw / cw) + 2;
        if (n % 2 === (wantOdd ? 0 : 1)) n++;
        for (let j = 0; j < n; j++) {
          const xc = cx + (j - (n - 1) / 2) * cw;
          const hx = ((xc - cx) / hw) * HW;
          if (!insideHeart(hx, hy)) continue;
          const x = xc - cw / 2;

          if (inVoidRows) {
            const overlap =
              Math.min(x + cw, vx + vw) - Math.max(x, vx);
            if (overlap > 0) {
              /* how much of this cell survives on the far side of the box */
              const left = Math.max(0, vx - x);
              const right = Math.max(0, x + cw - (vx + vw));
              const visible = Math.max(left, right);
              /* Anything left as a sliver is dropped rather than drawn. A
               * quarter-width strip of a portrait reads as a rendering fault,
               * and the gap it leaves sits flush against the box, so it is
               * read as part of the hole instead. */
              if (visible < cw * 0.38) continue;
            }
          }

          cells.push({
            x: x + GAP / 2,
            y: y0 + GAP / 2,
            w: cw - GAP,
            h: ch - GAP,
            row: r,
            p: 0,
            delay: 0,
            drop: 0,
            spin: 0,
            fx: 0,
            fy: 0,
            fw: 0,
            fh: 0,
            fdelay: 0,
            ftilt: 0,
            fz: 0,
            fdepth: 0,
          });
        }

        /* THE POINT, trimmed to its two innermost cells — see POINT_CELLS.
         * Done here rather than by skipping cells inside the loop above,
         * because "the two nearest the axis" is not knowable until the row has
         * been dealt: which slots survive the silhouette is what the loop is
         * working out. The survivors are put back in left-to-right order,
         * since the row's arrival sweep reads position from that order. */
        if (r === ROWS - 1 && cells.length - rowStart > POINT_CELLS) {
          const row = cells.splice(rowStart);
          row.sort(
            (a, b) =>
              Math.abs(a.x + a.w / 2 - cx) - Math.abs(b.x + b.w / 2 - cx)
          );
          const keep = row.slice(0, POINT_CELLS).sort((a, b) => a.x - b.x);
          for (const c of keep) cells.push(c);
        }
      }

      /* Photographs are dealt in ROW ORDER, so the first — and strongest —
       * frames land in the two lobes, which are the biggest cells and the
       * first thing anyone looks at. */
      /* ── the order of arrival ─────────────────────────────────────────
       * STRICTLY ROW BY ROW. Row one lands complete, then row two, and so on
       * down to the point. Two things had to go for that to read:
       *
       *   the random jitter — every cell used to take `rnd() * 0.12` on top
       *   of its delay, which is larger than the gap between neighbouring
       *   rows, so rows interleaved and the mosaic assembled as a scatter;
       *
       *   the CONTINUOUS delay — it was a function of the cell's pixel height
       *   down the heart, which is not the same thing as its row. The rows
       *   are not evenly spaced (the profile makes the top ones much taller),
       *   so a smooth ramp bunched the small lower rows almost on top of one
       *   another while stretching the big ones apart.
       *
       * The row INDEX is now the clock. Within a row the frames sweep left to
       * right across a fraction of one row-step — ordered, never random, and
       * short enough that a row is always finished before the next begins. */
      const rowStep = RISE_SPREAD / Math.max(1, ROWS - 1);
      const perRow: number[] = [];
      for (const c of cells) perRow[c.row] = (perRow[c.row] ?? 0) + 1;
      const seen: number[] = [];
      cells.forEach((c, i) => {
        c.p = i % PHOTOS.length;
        const n = perRow[c.row] ?? 1;
        const k = seen[c.row] ?? 0;
        seen[c.row] = k + 1;
        /* cells are built left to right within a row, so `k` IS the sweep */
        const frac = n > 1 ? k / (n - 1) : 0;
        c.delay = T_BLACK + c.row * rowStep + frac * rowStep * IN_ROW_SWEEP;
        /* Travel distance still varies — they all arrive on the beat, some
         * simply come further to get there. That is texture, not disorder. */
        c.drop = h * (0.4 + rnd() * 0.35);
        c.spin = (rnd() - 0.5) * 0.5;
      });

      /* ── the field ────────────────────────────────────────────────────
       * Where every frame ends up once the heart has burst.
       *
       * NON-OVERLAP IS STRUCTURAL HERE, not something checked for afterwards.
       * Every frame is CONTAINED INSIDE ITS OWN GRID SLOT, inset by half a
       * gutter — so two frames cannot touch, because their slots do not.
       * There is no separation pass and no collision test; the guarantee comes
       * from the geometry.
       *
       * The earlier version placed frames on a jittered grid and then shoved
       * any that landed on the heart out of the way, which broke exactly this
       * property: a shoved frame lands wherever it lands, including on top of
       * its neighbour.
       *
       * The heart is kept clear in TWO steps, and the split matters. A slot is
       * withheld only when the heart sits on its middle — at most one or two —
       * and every other frame near the heart is nudged WITHIN ITS OWN SLOT
       * until it clears. Withholding every slot the heart merely touched was
       * the first attempt and it blew a hole the size of four slots in the
       * wall: a slot is far larger than the heart, so dropping one clears many
       * times the area actually needed, and with the heart nudged below centre
       * all that surplus showed up above it as a bare column. Nudging inside
       * the slot keeps the containment guarantee — a frame still never leaves
       * its own box — while making the hole the size of the heart.
       *
       * The grid runs wider and taller than the viewport so frames are cropped
       * by every edge. A wall that stops politely inside the frame looks like a
       * slideshow; one the window cuts off looks like it continues. */
      const fn = cells.length;

      /* the heart's final footprint, solved BEFORE the field because the field
       * has to know which slots it may not use */
      const kscale = Math.max(
        1,
        (w * (mode === "tall" ? HEART_FINAL_W_TALL : HEART_FINAL_W)) / pixW
      );
      const kw = pixW * kscale;
      const kh = pixH * kscale;
      const kx = w / 2;
      const ky = h / 2 + kh * HEART_DROP;

      const OVER = 1.2; // how far the grid runs past the viewport
      const fieldW = w * OVER;
      const fieldH = h * OVER;
      const ox = -(fieldW - w) / 2;
      const oy = -(fieldH - h) / 2;

      /* Grow the grid until enough slots survive the heart. Starting from the
       * square-ish guess and adding columns keeps the slots close to the same
       * proportion rather than stretching them thin. */
      let cols = Math.max(3, Math.round(Math.sqrt(fn * (w / h))));
      let rowsF = Math.ceil(fn / cols);
      let slots: { gx: number; gy: number }[] = [];
      for (let attempt = 0; attempt < 8; attempt++) {
        const sw = fieldW / cols;
        const sh = fieldH / rowsF;
        slots = [];
        for (let gy = 0; gy < rowsF; gy++) {
          for (let gx = 0; gx < cols; gx++) {
            const sx = ox + gx * sw;
            const sy = oy + gy * sh;
            /* Withhold the slot only if the heart covers its MIDDLE — where a
             * frame would actually sit. Touching the slot's outer edge is not
             * enough; that case is solved by nudging, below. */
            const mcx = sx + sw / 2;
            const mcy = sy + sh / 2;
            const swallowed =
              Math.abs(mcx - kx) < kw / 2 + sw * 0.12 &&
              Math.abs(mcy - ky) < kh / 2 + sh * 0.12;
            if (!swallowed) slots.push({ gx, gy });
          }
        }
        if (slots.length >= fn) break;
        cols += 1;
        rowsF = Math.ceil((fn * 1.15) / cols);
      }
      const slotW = fieldW / cols;
      const slotH = fieldH / rowsF;

      /* THE GUTTER IS THE SPREAD. It is the minimum black between any two
       * frames, and because a frame may never leave its slot, raising it
       * genuinely opens the wall up rather than just shrinking the pictures
       * somewhere else. */
      const gut = Math.min(slotW, slotH) * 0.22;
      /* the biggest a frame may be and still sit inside its slot with the
       * gutter intact — driven by the SHORTER dimension so a wide viewport
       * does not produce letterbox-shaped photographs */
      const fMax = Math.max(
        12,
        Math.min(slotW - gut, (slotH - gut) / AR)
      );

      for (let i = slots.length - 1; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [slots[i], slots[j]] = [slots[j], slots[i]];
      }
      const order = cells.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }

      order.forEach((ci, n) => {
        const c = cells[ci];
        const slot = slots[n % slots.length];
        /* ── DEPTH ────────────────────────────────────────────────────
         * Biased toward the BACK, but only somewhat. Raising a uniform number
         * to a power puts most frames away from the lens and only a handful up
         * close, which is what a real room looks like — depth is legible
         * because near things are rare, and an even spread of sizes reads as
         * inconsistency rather than distance.
         *
         * The exponent was 3 first, and that is too much: it drove the median
         * frame to half the size of a near one, so the wall thinned out into a
         * scattering of specks with two big pictures in it. 1.8 keeps the bias
         * without emptying the room.
         *
         * The range is wide on purpose — a near frame is nearly two and a
         * half times a far one. Earlier it ran 0.64→1.0, which is a variation
         * you have to go looking for; below about 0.5 the difference stops
         * being a size and becomes a position. */
        const d = Math.pow(rnd(), 1.8);
        c.fdepth = d;
        c.fw = fMax * lerp(0.42, 1, d);
        c.fh = c.fw * AR;
        /* Jitter is bounded by the room actually left in the slot, so the
         * frame is off-centre but never outside. This is the line that makes
         * overlap impossible. */
        const jx = Math.max(0, (slotW - gut - c.fw) / 2);
        const jy = Math.max(0, (slotH - gut - c.fh) / 2);
        const homeX = ox + (slot.gx + 0.5) * slotW;
        const homeY = oy + (slot.gy + 0.5) * slotH;
        c.fx = homeX + (rnd() * 2 - 1) * jx;
        c.fy = homeY + (rnd() * 2 - 1) * jy;

        /* ── clear the heart, WITHOUT leaving the slot ──────────────────
         * Push along whichever axis needs the smaller move, then clamp back
         * into the slot's own jitter box. The clamp is what keeps the
         * no-overlap guarantee intact: the frame may end up only partly clear
         * of the heart, but it can never end up on a neighbour. In practice a
         * slot is bigger than the heart, so there is nearly always room. */
        const hpad = Math.min(w, h) * 0.012;
        const needX = kw / 2 + c.fw / 2 + hpad - Math.abs(c.fx - kx);
        const needY = kh / 2 + c.fh / 2 + hpad - Math.abs(c.fy - ky);
        if (needX > 0 && needY > 0) {
          if (needX <= needY) {
            const dir = c.fx >= kx ? 1 : -1;
            c.fx = clamp(c.fx + dir * needX, homeX - jx, homeX + jx);
          } else {
            const dir = c.fy >= ky ? 1 : -1;
            c.fy = clamp(c.fy + dir * needY, homeY - jy, homeY + jy);
          }
        }
        c.ftilt = (rnd() - 0.5) * 0.05;
        /* near paints last, so a near frame is never hidden behind a far one
         * — the single cheapest thing that sells the stack */
        c.fz = d;
        /* the middle of the heart lets go first, the point last — the burst
         * travels outward rather than everything leaving at once */
        const dcx = c.x + c.w / 2 - cx;
        const dcy = c.y + c.h / 2 - (top + hh * 0.45);
        const dist = Math.hypot(dcx, dcy) / Math.max(hw, hh);
        c.fdelay = T_SPREAD_IN + clamp01(dist) * SPREAD_STAGGER;
      });

      cells.sort((a, b) => a.fz - b.fz);

      /* ── the pixel heart ──────────────────────────────────────────────
       * The same curve, sampled at mosaic-of-one-pixel resolution inside the
       * blank box. Each pixel leaves a REAL photograph — a cell picked from
       * the mosaic around it — and carries that frame's average colour, so
       * the small heart is assembled out of the big one rather than merely
       * placed inside it. */
      /* FIT THE HEART TO ITS OWN PROPORTION, then centre it in the hole —
       * never stretch it to the hole. The hole is snapped to whole mosaic rows,
       * so its aspect is whatever those rows happen to add up to; mapping the
       * curve across that box directly made the small heart taller and narrower
       * than the big one, which quietly breaks the one claim the composition
       * makes — that these are the same shape at two resolutions. */
      /* Pixels across. The hole is one cell of the third row, so this is a
       * legibility budget, not a resolution: at thirteen across each square
       * came out about five pixels wide and the photograph tiles stopped
       * being photographs — they read as coloured dots. Nine is coarse enough
       * that a tile is recognisably an image and still fine enough to draw a
       * heart. */
      const ps = pixW / PIX_COLS;
      const pw = ps * PIX_COLS;
      const ph = ps * PIX_ROWS;
      const px0 = vx + (vw - pw) / 2;
      const py0 = vy + (vh - ph) / 2;

      const pix: Pix[] = [];
      const prnd = rng(77003);
      for (let r = 0; r < PIX_ROWS; r++) {
        for (let j = 0; j < PIX_COLS; j++) {
          /* the bitmap IS the shape — no curve, no sampling, no asymmetry */
          if (PIX_HEART[r][j] !== "#") continue;
          const xc = px0 + (j + 0.5) * ps;
          const yc = py0 + (r + 0.5) * ps;
          const from = cells.length
            ? cells[Math.floor(prnd() * cells.length)]
            : null;
          pix.push({
            x: xc - ps / 2,
            y: yc - ps / 2,
            s: ps,
            /* stagger outward from the pixel heart's own middle, so it fills
             * from the core rather than raining in from one side */
            delay:
              T_PIX_IN +
              (Math.hypot(xc - (px0 + pw / 2), yc - (py0 + ph / 2)) /
                Math.max(pw, ph)) *
                PIX_SPREAD +
              prnd() * 0.1,
            fx: from ? from.x + from.w / 2 : cx,
            fy: from ? from.y + from.h / 2 : vy,
            p: from ? from.p : 0,
            /* Its own clock. Seeded, so the pattern is identical on every load
             * and survives a resize — a heart whose tiles reshuffle when the
             * window moves reads as a glitch rather than as a composition. */
            cyc: TILE_MIN + prnd() * (TILE_MAX - TILE_MIN),
            phase: prnd(),
            seed: Math.floor(prnd() * 65536),
          });
        }
      }

      /* THE CARD IS CAPPED AGAINST BOTH AXES, on both solves. The tall branch
       * used to take two-thirds of the width and nothing else, which is fine on
       * a phone and wrong on anything squarer: on a 768x1024 tablet it came out
       * 507 by 710 — most of the frame — and the rail that sits under it on
       * this layout was left lying across its bottom edge. A portrait card has
       * to answer to the height it is standing in. */
      const cdw = Math.min(w * (mode === "tall" ? 0.66 : 0.3), h * 0.42);
      const cdh = cdw * 1.4;
      const cdx = w / 2 - cdw / 2;
      const cdy = h / 2 - cdh / 2;

      return {
        w, h, mode, gut, flank, copyTop: band, cx, top, hw, hh, cells,
        vx, vy, vw, vh, pix,
        kx, ky, kw, kh, kscale, cdx, cdy, cdw, cdh,
      };
    }

    function size() {
      const w = root.clientWidth || window.innerWidth;
      const h = root.clientHeight || window.innerHeight;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      cv.style.width = w + "px";
      cv.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      L = solve(w, h);
      root.style.setProperty("--am-heart-top", `${L.top}px`);
      root.style.setProperty("--am-heart-bot", `${L.top + L.hh}px`);
      root.style.setProperty("--am-copy-top", `${L.copyTop}px`);
      root.style.setProperty("--am-cardw", `${L.cdw}px`);
      root.style.setProperty("--am-cardh", `${L.cdh}px`);
      /* TWO LAYOUT DECISIONS THE STYLESHEET CANNOT MAKE FOR ITSELF, because
       * both are about how much room is left beside something the canvas
       * sized. A media query would have to guess at the heart and at the card;
       * here they are already solved, so the question is just arithmetic. */
      root.dataset.copy = L.flank ? "side" : "under";
      root.dataset.rail = (L.w - L.cdw) / 2 >= 108 ? "side" : "under";
    }

    /* ── cover-crop, computed per draw. Fifty of these a frame is nothing,
     * and baking them would mean re-baking every cell on every resize. */
    function cover(
      g: CanvasRenderingContext2D,
      im: CanvasImageSource,
      iw: number,
      ih: number,
      x: number,
      y: number,
      w: number,
      h: number
    ) {
      const k = Math.max(w / iw, h / ih);
      const sw = w / k;
      const sh = h / k;
      /* biased UP — a portrait's subject lives in the top half and a centred
       * crop takes the chin off */
      g.drawImage(im, (iw - sw) / 2, (ih - sh) * 0.36, sw, sh, x, y, w, h);
    }

    /* How far this frame leans toward the pointer. ONE function, because the
     * hit test has to agree with the paint to the pixel — a wall you can see
     * two centimetres from where you can click it is worse than no movement at
     * all.
     *
     * Everything about it is a pure function of the eased pointer and the
     * frame's OWN home position: the offset is never fed back in, so a frame
     * cannot chase the cursor it is moving toward. There is nothing to
     * converge — it is arithmetic, evaluated fresh every frame.
     *
     * `se` keeps it off the mosaic, `pullK` fades it with the pointer's
     * presence, and `openK` stops the wall while a card is up: motion in the
     * one part of the frame the visitor has just been told to stop looking at
     * is the thing they will watch. */
    function pullAt(c: Cell, se: number) {
      if (!L) return { x: 0, y: 0 };
      const s = se * pullK * (1 - smoother(openK));
      if (s <= 0.002) return { x: 0, y: 0 };
      const dx = pullE.x - c.fx;
      const dy = pullE.y - c.fy;
      const d = Math.hypot(dx, dy);
      const R = Math.min(L.w, L.h) * PULL_R;
      /* Outside the reach, and dead ON the frame, are the same answer: no
       * travel. The second is not really a guard against dividing by zero — it
       * is the effect's own logic. A frame the cursor has arrived at has
       * nowhere left to lean. */
      if (d >= R || d < 1) return { x: 0, y: 0 };
      /* smoothstep, so the pull arrives and leaves without an edge — a linear
       * falloff draws a visible ring on the wall where the reach ends */
      const t = 1 - d / R;
      const f = t * t * (3 - 2 * t);
      const amt = L.gut * PULL_CAP * f * s * lerp(PULL_FAR, 1, c.fdepth);
      return { x: (dx / d) * amt, y: (dy / d) * amt };
    }

    /* ── paint ────────────────────────────────────────────────────────── */
    function paint(T: number) {
      if (!L) return;
      const g = ctx;
      const { w, h } = L;

      g.fillStyle = STAGE;
      g.fillRect(0, 0, w, h);

      /* NO WASH BEHIND THE HEART. There was a purple radial bloom here, on the
       * theory that the mosaic should throw light onto the wall behind it. It
       * did the opposite of what it was for: it lifted the black off true, so
       * the frames sat on a faintly lit panel instead of in the dark, and
       * every dark photograph in the lower rows — which is most of them — lost
       * its edge against it. The stage is the section's only other colour, and
       * the strongest thing it can be is nothing at all. */

      /* The mosaic is DEAD STILL once it has landed. It used to beat — the
       * whole heart swelling on a lub-dub — and the movement fought the one
       * thing the composition is for: fifty photographs you are meant to look
       * at, resampling every frame forever. A heart made of pictures does not
       * need to be animated to read as a heart. */
      /* The chosen frame is drawn LAST, in its own pass below — it has to sit
       * over every other frame while it travels, and it must not take the dim
       * that the rest of the wall does. */
      /* ONE painter for every frame, selected or not. Extracted so the chosen
       * frame can be drawn AFTER the pixel heart: the heart is a layer over the
       * wall, and while it was painted last the opened card came up BEHIND it —
       * a white lattice sitting across somebody's face. Paint order is the
       * whole fix; nothing about the frame itself changed. */
      function paintCell(c: Cell, raw: number) {
        if (!L) return;
        const k = clamp01((T - c.delay) / T_RISE);
        if (k <= 0) return;
        const e = out4(k);
        const alpha = clamp01(k * 2.2);

        /* HOW FAR OUT OF THE HEART THIS FRAME HAS TRAVELLED. Everything about
         * the cell — where it is, how big it is, whether it is still clipped
         * against the hole — is one interpolation on this scalar, so the two
         * layouts never have to know about each other. */
        const s01 = clamp01((T - c.fdelay) / T_SPREAD);
        const se = smoother(s01);

        const hxc = c.x + c.w / 2;
        const hyc = c.y + c.h / 2 + (1 - e) * c.drop;
        const pp = pullAt(c, se);
        let cxc = lerp(hxc, c.fx, se) + pp.x;
        let cyc = lerp(hyc, c.fy, se) + pp.y;
        let dw = lerp(c.w, c.fw, se);
        let dh = lerp(c.h, c.fh, se);

        /* THE OPEN FRAME GROWS OUT OF THE WALL. Same shape of solve as the
         * spread: one eased scalar carrying the frame from where it is lying
         * to where the card sits. It is not a card that appears over a wall —
         * it is the photograph you pointed at, travelling. */
        /* how far THIS frame is toward the card — its own scalar now, so the
         * one flying home and the one flying out can be in the air together */
        const ok = raw > 0.0001 ? smoother(raw) : 0;
        if (ok > 0.0001) {
          cxc = lerp(cxc, L.cdx + L.cdw / 2, ok);
          cyc = lerp(cyc, L.cdy + L.cdh / 2, ok);
          dw = lerp(dw, L.cdw, ok);
          dh = lerp(dh, L.cdh, ok);
        }
        const sc = lerp(0.86, 1, e);
        /* Hover only bites once the field has settled. Compared by IDENTITY
         * rather than by index — the painter no longer knows its own index,
         * and the cell object is the thing `hover` actually refers to. */
        const lift =
          hover >= 0 && L.cells[hover] === c && s01 > 0.99 ? hoverK : 0;

        const src = PHOTOS[c.p];
        const im = imgs.get(src);
        const sf = soft.get(src);

        g.save();
        /* Straddlers are clipped against the hole so it keeps a true
         * rectangular edge instead of the mosaic's staircase — but only while
         * the frame is still IN the heart. Once it has left, the hole it was
         * cut against no longer exists. */
        if (
          se < 0.001 &&
          c.x < L.vx + L.vw &&
          c.x + c.w > L.vx &&
          c.y < L.vy + L.vh &&
          c.y + c.h > L.vy
        ) {
          g.beginPath();
          g.rect(0, 0, w, h);
          g.rect(L.vx, L.vy, L.vw, L.vh);
          g.clip("evenodd");
        }
        g.globalAlpha = alpha;
        g.translate(cxc, cyc);
        if (c.spin) g.rotate(c.spin * (1 - e));
        /* 3. A near frame leans a little more than a far one: the same
         * physical tilt subtends a bigger angle closer to the lens. The lean
         * is unwound as the frame opens — a card being held up is square to
         * you. */
        if (c.ftilt) g.rotate(c.ftilt * se * (0.55 + c.fdepth * 0.8) * (1 - ok));

        /* the corner radius grows with the card, so the frame becomes a card
         * rather than cutting to one */
        if (ok > 0.0001) {
          const rr = 10 * ok;
          g.beginPath();
          g.moveTo(-dw / 2 + rr, -dh / 2);
          g.arcTo(dw / 2, -dh / 2, dw / 2, dh / 2, rr);
          g.arcTo(dw / 2, dh / 2, -dw / 2, dh / 2, rr);
          g.arcTo(-dw / 2, dh / 2, -dw / 2, -dh / 2, rr);
          g.arcTo(-dw / 2, -dh / 2, dw / 2, -dh / 2, rr);
          g.closePath();
          g.clip();
        }
        g.scale(sc * (1 + lift * 0.06), sc * (1 + lift * 0.06));

        /* ── the three depth cues that are not size ───────────────────
         * All of them fade in with `se`, so they belong to the FIELD only —
         * inside the heart every frame is on one plane and any of this would
         * just look like dirty glass.
         *
         * `far` is how far back this frame sits, eased so the effect stays off
         * the near half and concentrates where it reads. */
        let far = se * Math.pow(1 - c.fdepth, 1.7);
        /* opening pulls the frame to the very front of the room, so its
         * distance haze has to come off as it travels */
        far *= 1 - ok;

        /* 1. SHARPNESS. The 32px bake is already underneath every frame for
         * the rise, and holding the sharp copy back over it is a free
         * depth-of-field: a far frame resolves less. This is why there is no
         * blur filter anywhere in the section — the soft layer was already
         * paid for. */
        if (sf) cover(g, sf, 32, 40, -dw / 2, -dh / 2, dw, dh);
        if (im && im.complete && im.naturalWidth) {
          g.globalAlpha =
            alpha * out2(clamp01((k - 0.25) / 0.6)) * (1 - far * 0.4);
          cover(g, im, im.naturalWidth, im.naturalHeight, -dw / 2, -dh / 2, dw, dh);
        }

        /* 2. ATMOSPHERE. Distance puts room between you and the frame, and a
         * dark room fills it with dark. Veiling the far frames toward the
         * stage colour is aerial perspective — and it is the cue that stops a
         * small frame reading as merely small. */
        if (far > 0.004) {
          g.globalAlpha = Math.min(1, far * 0.34);
          g.fillStyle = STAGE;
          g.fillRect(-dw / 2, -dh / 2, dw, dh);
          g.globalAlpha = alpha;
        }
        /* Only the OPEN card dims the wall. Hovering used to knock back every
         * other frame as well, and on a wall of forty-five photographs that
         * meant the whole section flickered darker every time the pointer
         * crossed it — a lot of movement to say "this one is clickable", which
         * the lift already says on its own. */
        /* A frame takes the wall's dim in proportion to how far it is NOT
         * lifted, which hands a returning photograph back to the wall smoothly
         * instead of dropping it into the dark at the end of its trip. The wall
         * follows whichever card is most open, so the dim rides out the return
         * rather than snapping off the moment the card is dismissed. */
        const dim = (1 - ok) * Math.max(openK, retK) * 0.72;
        if (dim > 0.002) {
          g.globalAlpha = Math.min(1, dim);
          g.fillStyle = STAGE;
          g.fillRect(-dw / 2, -dh / 2, dw, dh);
        }
        g.restore();
      }

      for (let ci = 0; ci < L.cells.length; ci++) {
        if (ci === sel || ci === retIdx) continue; // drawn last, over the heart
        paintCell(L.cells[ci], 0);
      }

      /* ── the pixel heart ──────────────────────────────────────────────
       * White paper squares, with a scattered few carrying a photograph. The
       * white is what makes the small heart read at all — it is nine pixels
       * across, sitting in a hole in a wall of saturated frames, and a heart
       * built entirely out of averaged photo colours simply dissolves into the
       * mosaic around it. White separates it; the picture tiles then say what
       * it is made of. */
      /* THE HEART GROWS. As the photographs burst outward the pixel heart
       * scales up about the middle of its hole and fades as it goes — the
       * shape you assembled blooms past the camera and hands the frame to the
       * wall it was made of. It is one transform on the whole layer rather
       * than per-pixel maths, so growing it costs nothing. */
      const gk = smoother(clamp01((T - T_SPREAD_IN) / (T_SPREAD * 0.85)));
      const hcx = L.vx + L.vw / 2;
      const hcy = L.vy + L.vh / 2;
      const gs = 1 + gk * (L.kscale - 1);
      g.save();
      /* THE SMALL HEART DOES NOT ANSWER THE POINTER. It rode the mosaic's own
       * plane while the whole room shifted, because a heart sitting IN the
       * mosaic that disagreed with it would slide out of its own hole. With the
       * movement local to the cursor there is no plane to ride, and the right
       * answer is the still one: the shape the section is spent assembling is
       * not a thing you nudge. */
      /* It grows AND travels: the hole it was assembled in sits high in the
       * mosaic, and the heart has to finish in the middle of the SECTION, not
       * the middle of a heart that no longer exists. Scaling in place and
       * leaving it there would park it above centre for good. */
      /* Flies to `kx,ky` — the very point the field was solved to keep clear.
       * Writing `w/2, h/2` here instead would put the heart somewhere the wall
       * had not made room for, and the two would only agree by accident. */
      g.translate(lerp(hcx, L.kx, gk), lerp(hcy, L.ky, gk));
      g.scale(gs, gs);
      g.translate(-hcx, -hcy);
      /* THE HEART DOES NOT LEAVE. It grew and dissolved in an earlier build —
       * which read well as a beat but threw away the payoff: the shape the
       * whole section is spent assembling was on screen for half a second and
       * then gone. It now grows to size and stays, and the field is solved to
       * keep clear of it. The only thing that touches its opacity is the
       * detail card, which dims everything behind it including this. */
      const heartFade = 1 - Math.max(openK, retK) * 0.72;

      for (const p of L.pix) {
        if (heartFade <= 0.002) break;
        const k = clamp01((T - p.delay) / T_PIX);
        if (k <= 0) continue;
        const e = out4(k);
        const x = lerp(p.fx, p.x, e);
        const y = lerp(p.fy, p.y, e);
        /* It leaves the photograph as a speck and arrives at full size, minus
         * a hairline. The gap is what makes these read as PIXELS: butted edge
         * to edge, the white squares merge into one solid lump of paper and
         * the shape stops being made of anything. */
        /* The gap is a proportion of the square at heart size, but the whole
         * layer is drawn through a 7x transform once it has grown — so a gap
         * that read as a hairline in the hole came out as visible mortar on
         * the wall, and the heart turned into a tiled bathroom floor. Divide
         * it back out and the seam stays a seam at every size. */
        const gapPx = Math.max(0.75, p.s * 0.1) / gs;
        const full = Math.max(1, p.s - gapPx);
        const s = Math.max(1, full * lerp(0.25, 1, e));
        const dx = x + (p.s - s) / 2;
        const dy = y + (p.s - s) / 2;
        g.globalAlpha = clamp01(k * 2.4) * heartFade;

        /* ── this tile's own cycle ──────────────────────────────────────
         * `u` runs 0→1 over `cyc` seconds from its own phase. The photograph
         * fades up, holds, fades out, and the rest of the cycle is paper. The
         * picture is chosen per CYCLE, so a tile shows a different frame each
         * time it comes back and the heart keeps turning over its own roll. */
        const cycles = (T - p.delay) / p.cyc + p.phase;
        const turn = Math.floor(cycles);
        const u = (cycles - turn) * p.cyc; // seconds into this cycle
        let reveal = 0;
        if (u < TILE_IN) reveal = u / TILE_IN;
        else if (u < TILE_IN + TILE_HOLD) reveal = 1;
        else if (u < TILE_IN + TILE_HOLD + TILE_OUT)
          reveal = 1 - (u - TILE_IN - TILE_HOLD) / TILE_OUT;
        reveal = clamp01(reveal);

        /* Paper is ALWAYS drawn, and the photograph is composited over it.
         * Cross-fading paper against picture instead would dip the tile toward
         * the stage black at the halfway point, and a heart that flickers dark
         * as its tiles turn over reads as a fault rather than as life. */
        g.fillStyle = INK;
        g.fillRect(dx, dy, s, s);

        if (reveal > 0.004) {
          /* a cheap integer hash of (tile, cycle) — deterministic, so the same
           * tile on the same turn always draws the same frame */
          let hsh = (p.seed ^ (turn * 0x9e3779b1)) >>> 0;
          hsh ^= hsh >>> 15;
          hsh = Math.imul(hsh, 0x2c1b3c6d) >>> 0;
          hsh ^= hsh >>> 12;
          const src = PHOTOS[hsh % PHOTOS.length];
          const bm = soft.get(src) ?? imgs.get(src);
          if (bm) {
            const iw = bm instanceof HTMLCanvasElement ? bm.width : bm.naturalWidth;
            const ih = bm instanceof HTMLCanvasElement ? bm.height : bm.naturalHeight;
            if (iw && ih) {
              g.globalAlpha = clamp01(k * 2.4) * heartFade * reveal;
              /* the 32px bake, not the full frame: at this size the difference
               * is invisible and the bake is already decoded */
              cover(g, bm, iw, ih, dx, dy, s, s);
            }
          }
        }
      }
      g.restore();
      g.globalAlpha = 1;

      /* AND NOW THE CHOSEN FRAME, over everything — including the pixel heart,
       * which sits at the centre of the section exactly where the card lands.
       * Drawn before the heart it came up behind a white lattice across the
       * subject's face. This one line is the whole reason `paintCell` was
       * pulled out of its loop. */
      if (retIdx >= 0 && L.cells[retIdx]) paintCell(L.cells[retIdx], retK);
      if (sel >= 0 && L.cells[sel]) paintCell(L.cells[sel], openK);
    }

    /* ── loop ─────────────────────────────────────────────────────────── */
    let last = performance.now();
    let T = 0;
    /** dev-only: the capture scripts freeze the clock on an exact beat */
    let held = false;
    let copyGone = false;

    /* WHAT THE SECTION IS SHOWING, which is not always what the clock says.
     * With reduced motion the composition opens already finished, so the beat
     * every consumer reads is the END rather than a clock still counting up to
     * it — and there are three consumers, not one: the paint, the DOM copy,
     * and the hit test. Reading them from separate places is what left a
     * reduced-motion visitor with a finished wall that could not be clicked
     * for its first eight seconds, with the heart's headline still lying
     * across it. One accessor, so they cannot disagree. */
    const beat = () => (reduce ? T_END : T);

    /* The headline and the note belong to the HEART, not to the wall. Once
     * the frames spread they cover the whole section, and type laid over
     * fifty photographs is not type any more — so the copy leaves as the wall
     * arrives. The header bar stays: it is the only thing that still says
     * whose wall this is.
     *
     * A FUNCTION OF T, like everything else, rather than a step taken once on
     * the way past. The copy is the only part of the section that lives in the
     * DOM instead of on the canvas, and if it is advanced by the loop alone it
     * is the one thing a seek cannot rewind — which is exactly what happened:
     * a capture that seeked back to the whole heart got the heart with its
     * headline already gone, because the clock had run past the burst while
     * the page was settling. Driven from T, the copy is wherever the beat
     * says it is, in either direction. */
    function syncCopy(t: number) {
      const away = t > T_SPREAD_IN + 0.15;
      if (away !== copyGone) {
        copyGone = away;
        root.dataset.spread = away ? "1" : "";
      }
    }

    function loop(now: number) {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (paused || held) return;
      T += dt;
      syncCopy(beat());
      /* the two interaction scalars ease frame-rate-independently, so a lift
       * or a dim looks the same at 60Hz and 120Hz */
      const chase = 1 - Math.exp(-dt * 11);
      hoverK += ((hover >= 0 ? 1 : 0) - hoverK) * chase;
      /* Slower than the hover chase: this one carries a photograph across the
       * screen and doubles its size, and at the hover rate that reads as a
       * snap rather than a move. */
      const openChase = 1 - Math.exp(-dt * 5.4);
      openK += ((sel >= 0 ? 1 : 0) - openK) * openChase;
      /* The pull follows the hand closely — it is answering a gesture at a
       * particular frame, so it has to be under the cursor and not trailing a
       * quarter-second behind it — while the PRESENCE fades slowly, so leaving
       * the section releases the frames instead of dropping them. */
      const pullChase = 1 - Math.exp(-dt * 9);
      pullE.x += (ptr.x - pullE.x) * pullChase;
      pullE.y += (ptr.y - pullE.y) * pullChase;
      pullK += (pullIn - pullK) * (1 - Math.exp(-dt * 4.5));
      /* the retired frame finishes its trip home on the same rate the card
       * flew out on, and is let go once it has arrived */
      retK += (0 - retK) * openChase;
      if (retIdx >= 0 && retK < 0.004) {
        retK = 0;
        retIdx = -1;
      }
      paint(beat());
    }

    /* ── hit testing ──────────────────────────────────────────────────────
     * Walked BACK to front, because the field paints front-last: the frame
     * you can see on top is the one you must hit. Only the settled field is
     * clickable — while the cells are still travelling there is nothing
     * stable to aim at, and letting a moving target be clicked makes the
     * section feel like it is dodging you. */
    function pick(px: number, py: number) {
      if (!L || beat() < T_END) return -1;
      for (let i = L.cells.length - 1; i >= 0; i--) {
        const c = L.cells[i];
        /* the settled field is fully spread, so the frame's parallax is read
         * at se = 1 — the same offset the paint gave it this frame */
        const pp = pullAt(c, 1);
        const dx = px - (c.fx + pp.x);
        const dy = py - (c.fy + pp.y);
        if (Math.abs(dx) <= c.fw / 2 && Math.abs(dy) <= c.fh / 2) return i;
      }
      return -1;
    }

    /** Hand the open frame over to the return slot. The card is finished with
     *  it; the photograph is not. */
    function retire() {
      if (sel < 0) return;
      retIdx = sel;
      retK = openK;
      sel = -1;
      openK = 0;
    }

    function openAt(i: number) {
      if (!L || i < 0 || i >= L.cells.length) return;
      /* Clicking the frame that is still on its way home CATCHES it: it keeps
       * the height it has left rather than dropping to the wall and setting off
       * again, so a change of mind is one continuous move. Any other frame
       * starts from zero, and the one in flight carries on home behind it. */
      if (i === retIdx) {
        openK = retK;
        retK = 0;
        retIdx = -1;
      } else {
        retire();
        openK = 0;
      }
      sel = i;
      hover = -1;
      root.dataset.hit = "";
      setUi({ open: true, photo: L.cells[i].p });
      root.dataset.open = "1";
    }
    function close() {
      if (sel < 0) return;
      /* The selection is dropped HERE, not when the frame lands — that one
       * second of flight used to be a second in which nothing on the wall could
       * be clicked or even lit. */
      retire();
      setUi({ open: false, photo: -1 });
      root.dataset.open = "";
      /* Re-resolve hover from where the pointer already is. Closing the card
       * puts a frame back under a cursor that never moved, and without this it
       * stays unlit until you jiggle the mouse — the section looks like it has
       * stopped responding at exactly the moment it handed control back. */
      hover = pick(ptr.x, ptr.y);
      root.dataset.hit = hover >= 0 ? "1" : "";
    }
    /** ← / → walk the field in paint order, wrapping. */
    function step(d: number) {
      if (!L || sel < 0) return;
      /* Stepping keeps `openK` where it is, so the card does not fly out and
       * back in between neighbours — the photograph inside it simply changes.
       * Re-running the whole entrance for every arrow press would make the
       * keyboard feel like it was fighting the animation. */
      sel = (sel + d + L.cells.length) % L.cells.length;
      setUi({ open: true, photo: L.cells[sel].p });
      root.dataset.open = "1";
    }

    /* the last place the pointer was, so hover can be re-resolved without one */
    const ptr = { x: -9999, y: -9999 };
    const onMove = (e: PointerEvent) => {
      const r = root.getBoundingClientRect();
      ptr.x = e.clientX - r.left;
      ptr.y = e.clientY - r.top;
      /* Reduced motion opts out of the whole thing: a composition that has been
       * asked to hold still may not drift either. On arrival the eased pointer
       * is SNAPPED rather than chased, so the first move does not drag the
       * field across the section from wherever the cursor was last. */
      if (!reduce) {
        if (pullIn === 0) {
          pullE.x = ptr.x;
          pullE.y = ptr.y;
        }
        pullIn = 1;
      }
      const i = sel >= 0 ? -1 : pick(ptr.x, ptr.y);
      if (i !== hover) {
        hover = i;
        root.dataset.hit = i >= 0 ? "1" : "";
      }
    };
    const onLeave = () => {
      hover = -1;
      root.dataset.hit = "";
      /* the frames settle back rather than staying leaned at whatever the
       * pointer was doing when it crossed the edge */
      pullIn = 0;
    };
    /* OPEN ON click, NOT ON pointerdown — and on a touch screen that is not a
     * preference, it is the whole difference between working and not.
     *
     * Opening on pointerdown mounts the card's scrim under a finger that has
     * not lifted yet. The tap then finishes: touchend fires, and the browser
     * dispatches the gesture's compatibility mouse events — including a click,
     * about twenty-five milliseconds later — at that same point, where the
     * scrim is now the topmost element. The scrim's whole job is to dismiss the
     * card, so every tap opened a photograph and immediately closed it again.
     * A mouse never showed it: there, click is dispatched on the nearest common
     * ancestor of the mousedown and mouseup targets, which is the root rather
     * than a scrim that did not exist at mousedown.
     *
     * Listening for the click itself puts the open at the END of the gesture,
     * so there is no later event in it to catch. The alternative — keeping
     * pointerdown and calling preventDefault to suppress the compatibility
     * events — would also stop the page scrolling from a touch that began on
     * the section, which is a worse thing to break than a few milliseconds of
     * response.
     *
     * The guard still reads `sel >= 0`, and it has to: a click on the scrim
     * bubbles through this listener on its way to React's own handler, so
     * without it a tap meant to dismiss the card would re-open whatever frame
     * happens to lie under it. */
    const onTap = (e: MouseEvent) => {
      if (sel >= 0) return;
      const r = root.getBoundingClientRect();
      const i = pick(e.clientX - r.left, e.clientY - r.top);
      if (i >= 0) openAt(i);
    };
    const onKey = (e: KeyboardEvent) => {
      if (sel < 0) return;
      if (e.key === "Escape") {
        close();
        e.preventDefault();
      } else if (e.key === "ArrowRight") {
        step(1);
        e.preventDefault();
      } else if (e.key === "ArrowLeft") {
        step(-1);
        e.preventDefault();
      }
    };
    root.addEventListener("pointermove", onMove);
    root.addEventListener("pointerleave", onLeave);
    root.addEventListener("click", onTap);
    window.addEventListener("keydown", onKey);
    apiRef.current = { close, step, open: openAt };

    size();
    setReady(true);

    const ro = new ResizeObserver(() => size());
    ro.observe(root);
    const io = new IntersectionObserver(
      (es) => {
        paused = !es[0].isIntersecting;
        if (!paused) last = performance.now();
      },
      { threshold: 0.01 }
    );
    io.observe(root);
    const onVis = () => {
      if (document.hidden) cancelAnimationFrame(raf);
      else {
        last = performance.now();
        raf = requestAnimationFrame(loop);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    raf = requestAnimationFrame(loop);

    /* Dev-only, compiled out of production. The capture scripts need to land
     * on an EXACT beat: measuring wall-clock from page load samples whatever
     * the section happens to be doing once the network has settled, which is
     * never the beat you asked for and is different on every run. Seeking the
     * section's own clock and holding it there makes a shot reproducible. */
    if (process.env.NODE_ENV !== "production") {
      const win = window as unknown as {
        __am?: () => unknown;
        __amSeek?: (v: number) => void;
        __amPlay?: () => void;
      };
      win.__am = () => ({
        T: +T.toFixed(2),
        held,
        decoded,
        cells: L?.cells.length,
        pix: L?.pix.length,
        mode: L?.mode,
        rows: L
          ? Array.from({ length: 12 }, (_, r) => {
              const cs = (L as Layout).cells.filter((c) => c.row === r);
              if (!cs.length) return null;
              const xs = cs.map((c) => c.x).sort((a, b) => a - b);
              const gaps = [];
              for (let i = 1; i < xs.length; i++) {
                const d = xs[i] - xs[i - 1];
                if (d > cs[0].w * 1.4) gaps.push(+(d / (cs[0].w + 3)).toFixed(2));
              }
              const right = Math.max(...cs.map((c) => c.x + c.w));
              return {
                row: r + 1,
                n: cs.length,
                w: Math.round(cs[0].w),
                gapsInCells: gaps,
                x0: Math.round(xs[0]),
                x1: Math.round(right),
              };
            }).filter(Boolean)
          : null,
      });
      win.__amSeek = (v: number) => {
        T = v;
        held = true;
        /* the DOM copy is part of the frame, so it is seeked too — see
         * `syncCopy` for why this is not the loop's job alone */
        syncCopy(v);
        paint(v);
      };
      /* the blank box in page pixels, so a capture can crop tight on the
       * pixel heart instead of screenshotting the whole stage */
      (win as unknown as { __amBox?: () => unknown }).__amBox = () =>
        L ? { x: L.vx, y: L.vy, w: L.vw, h: L.vh } : null;
      (win as unknown as { __amKeep?: () => unknown }).__amKeep = () =>
        L ? { kx: L.kx, ky: L.ky, kw: L.kw, kh: L.kh, kscale: L.kscale } : null;
      (win as unknown as { __amDelays?: () => unknown }).__amDelays = () =>
        L ? L.cells.map((c) => ({ row: c.row, delay: c.delay })) : null;
      /* what the pointer is actually doing to each frame, so a capture can
       * check the pull instead of squinting at two screenshots */
      (win as unknown as { __amPull?: () => unknown }).__amPull = () =>
        L
          ? L.cells.map((c) => {
              const o = pullAt(c, 1);
              return { x: c.fx, y: c.fy, dx: +o.x.toFixed(2), dy: +o.y.toFixed(2) };
            })
          : null;
      (win as unknown as { __amField?: () => unknown }).__amField = () =>
        L
          ? L.cells.map((c) => ({ x: c.fx, y: c.fy, w: c.fw, h: c.fh }))
          : null;
      win.__amPlay = () => {
        held = false;
        last = performance.now();
      };
    }

    return () => {
      cancelAnimationFrame(raf);
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", onLeave);
      root.removeEventListener("click", onTap);
      window.removeEventListener("keydown", onKey);
      apiRef.current = null;
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      imgs.clear();
      soft.clear();
    };
  }, []);

  const meta = ui.photo >= 0 ? (META[ui.photo] ?? null) : null;

  return (
    <div
      ref={rootRef}
      className={`am-root ${className ?? ""}`}
      data-ready={ready ? "1" : undefined}
      aria-label="A heart assembled from photographs"
    >
      <canvas ref={cvRef} className="am-cv" aria-hidden />

      <header className="am-bar">
        <span className="am-mark">
          <i aria-hidden>✳</i>
          {STUDIO.mark}
        </span>
      </header>

      <h2 className="am-head am-head-l">
        <span>{STUDIO.headLeft[0]}</span>
        <em>{STUDIO.headLeft[1]}</em>
      </h2>
      <h2 className="am-head am-head-r">
        <span>{STUDIO.headRight[0]}</span>
        <em>{STUDIO.headRight[1]}</em>
      </h2>

      <p className="am-note">
        {STUDIO.note} <a href="#!">{STUDIO.noteAccent}</a>
      </p>

      {/* THE DETAIL CARD IS DOM, NOT CANVAS. Everything on it is type, icons
          and controls — the three things a canvas is worst at. As DOM it gets
          real text rendering, real focus order and real hit targets for free,
          and the canvas underneath goes on being a wall of photographs. */}
      {ui.open && meta && (
        <div className="am-view" role="dialog" aria-modal="true" aria-label={meta.name}>
          <button
            type="button"
            className="am-scrim"
            aria-label="Close"
            onClick={() => apiRef.current?.close()}
          />

          <button type="button" className="am-collect">
            <i aria-hidden>+</i> {STUDIO.collect}
          </button>

          <div className="am-rail" aria-hidden>
            <span className="am-railbtn">♡</span>
            <span className="am-railbtn">↗</span>
          </div>

          {/* THE PHOTOGRAPH IS NOT IN HERE. The canvas flies the frame you
              clicked into this exact box and holds it there, so the card is
              only the type and the controls laid over it. Putting an <img> here
              too would mean two copies of the same picture arriving by
              different routes — and the moment they disagreed by a pixel the
              handover would flicker. The alt text lives on the figure instead,
              so the frame is still announced. */}
          <figure className="am-card" aria-label={meta.name}>
            <span className="am-seg" aria-hidden>
              <b />
              <b />
              <b />
            </span>
            <figcaption>
              <h3>
                {meta.name}
                <svg viewBox="0 0 16 16" aria-hidden>
                  <circle cx="8" cy="8" r="8" />
                  <path d="M4.6 8.2l2.2 2.2 4.6-4.6" />
                </svg>
              </h3>
              <ul>
                {meta.tags.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </figcaption>
            <span className="am-acts" aria-hidden>
              <b>⤢</b>
              <b>↓</b>
            </span>
          </figure>

          <p className="am-hint">
            Press <kbd>←</kbd> <kbd>→</kbd> {STUDIO.hint}
          </p>
        </div>
      )}

      <style>{`
        .am-root {
          position: absolute;
          inset: 0;
          overflow: hidden;
          background: ${STAGE};
          color: ${INK};
          -webkit-tap-highlight-color: transparent;
          font-family: var(--font-geist-sans), system-ui, sans-serif;
        }
        .am-cv { position: absolute; inset: 0; display: block; }

        /* All chrome resolves out of the same black the photographs come out
           of, a beat after them — the stage is empty first, and nothing is
           allowed to be on it before the heart starts. */
        .am-root > *:not(.am-cv) {
          opacity: 0;
          transform: translateY(10px);
          filter: blur(6px);
        }
        .am-root[data-ready="1"] > *:not(.am-cv) {
          opacity: 1;
          transform: none;
          filter: none;
          transition: opacity .9s cubic-bezier(.16,1,.3,1) .35s,
                      transform 1.1s cubic-bezier(.16,1,.3,1) .35s,
                      filter .9s cubic-bezier(.16,1,.3,1) .35s;
        }
        .am-root[data-ready="1"] .am-head { transition-delay: 1.5s; }
        .am-root[data-ready="1"] .am-note { transition-delay: 1.9s; }

        /* THE COLUMN IS NAMED, not left to auto-placement. The bar holds one
           thing — the wordmark — on a three-column grid whose outer columns are
           empty, which is what puts the mark on the SECTION'S centre line. A
           lone auto-placed child would take column one and sit at the left
           edge instead. Naming it is also what let the CTA that used to sit in
           column three be taken out without moving anything else. */
        .am-bar {
          position: absolute; top: 0; left: 0; right: 0; z-index: 4;
          display: grid; grid-template-columns: 1fr auto 1fr;
          align-items: center; gap: 16px;
          padding: 2.2vh 2.4vw;
        }
        .am-mark {
          grid-column: 2;
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 11px; letter-spacing: .3em; text-transform: uppercase;
          font-weight: 500;
        }
        .am-mark i { font-style: normal; opacity: .8; font-size: 12px; }
        /* The headline is SPLIT around the heart — it is the frame the shape
           sits in, so it never competes for the middle of the stage. */
        .am-head {
          position: absolute; bottom: 8vh; z-index: 3;
          display: flex; flex-direction: column;
          font-family: var(--font-instrument-serif), Georgia, serif;
          font-weight: 400; line-height: .92; letter-spacing: -.01em;
          margin: 0;
        }
        /* Sized off BOTH axes. Width alone put 88px type in a 400px-tall
           landscape phone, where the copy then took a third of the frame —
           a vw alone cannot see that the frame is short. The two ratios are the same
           ratio (3.6/5.6 = 6.4/10), so the pair keeps its proportion whichever
           axis is doing the limiting. */
        .am-head span { font-size: clamp(28px, min(3.6vw, 6.4vh), 58px); opacity: .5; }
        .am-head em {
          font-style: normal;
          font-size: clamp(38px, min(5.6vw, 10vh), 88px);
        }
        /* THE COPY STOPS DRIFTING AT A MAX WIDTH. Pinned to the viewport's own
           edge it kept walking outward on a wide monitor — on an ultrawide the
           headline sat seven hundred pixels from the heart it is supposed to be
           framing, one word marooned in each corner. Past 1800 the page is
           treated as 1800 and the margin grows around it instead. */
        .am-head-l { left: max(2.6vw, calc((100vw - 1800px) / 2)); }
        .am-head-r {
          right: max(2.6vw, calc((100vw - 1800px) / 2));
          text-align: right;
        }

        /* Centred with left/right + text-align, NOT translateX(-50%). The
           entrance rule below resolves every child to transform:none, which
           silently cancels any transform used for POSITIONING — the note was
           sitting half a screen right of where it belonged on every viewport
           because of it. Nothing here may lean on transform to place itself. */
        .am-note {
          position: absolute; left: 0; right: 0; bottom: 9vh;
          z-index: 3; margin: 0; text-align: center; white-space: nowrap;
          font-size: 11.5px; letter-spacing: .04em; opacity: .62;
        }
        .am-note a { color: inherit; text-underline-offset: 4px; }

        /* ── the copy, STACKED ────────────────────────────────────────────
           Used when the canvas has told us the headline will not fit beside
           the heart (data-copy="under"). It is not a phone rule: a 1000x900
           window lands here too, and a 844x390 phone in landscape does not.

           The --am-copy-top property is the canvas's own band edge in pixels, so type
           starts exactly where the heart's room stops. The second line is
           offset by the FIRST line's measured height — two line boxes at .92
           plus a gap — rather than by a flat slice of the viewport, which is
           what let the two halves of the sentence collide on a 320-wide phone
           where the type had bottomed out at its clamp but the slice had
           not. */
        .am-root[data-copy="under"] {
          --am-h-span: clamp(22px, 6vw, 34px);
          --am-h-em: clamp(30px, 9vw, 50px);
        }
        .am-root[data-copy="under"] .am-head {
          bottom: auto; top: var(--am-copy-top, 72%);
          left: 6vw; right: auto; text-align: left;
        }
        .am-root[data-copy="under"] .am-head span { font-size: var(--am-h-span); }
        .am-root[data-copy="under"] .am-head em { font-size: var(--am-h-em); }
        .am-root[data-copy="under"] .am-head-r {
          top: calc(
            var(--am-copy-top, 72%) +
            0.92 * (var(--am-h-span) + var(--am-h-em)) +
            0.45 * var(--am-h-em)
          );
        }
        .am-root[data-copy="under"] .am-note {
          bottom: 4vh; left: 6vw; right: 6vw;
          white-space: normal; font-size: 10.5px; line-height: 1.6;
        }

        /* the wall is only pointable once it IS a wall */
        .am-root[data-hit="1"] { cursor: pointer; }
        /* the copy belongs to the heart; it leaves when the wall arrives */
        .am-root[data-spread="1"] .am-head,
        .am-root[data-spread="1"] .am-note {
          opacity: 0; pointer-events: none;
          transition: opacity .55s ease;
        }
        /* the page chrome steps aside for the card */
        .am-root[data-open="1"] .am-head,
        .am-root[data-open="1"] .am-note { opacity: 0; pointer-events: none; }

        /* ── the detail card ──────────────────────────────────────────── */
        /* ONE source for the card's width, because the pill above it and the
           rail beside it are both positioned off the card's edges. Written out
           twice by hand they drift the moment the card is resized — which is
           exactly what happened: the pill was placed against a guessed half-
           height and ended up sitting ON the photograph. */
        /* The card's size comes from the CANVAS SOLVE, handed over as custom
           properties — the same numbers the travelling frame flies to. Two
           definitions of this box is one too many: the stylesheet's version
           and the canvas's version drifted by a few pixels at some viewport
           sizes and the handover jumped. */
        .am-view {
          position: absolute; inset: 0; z-index: 20;
          --cardw: var(--am-cardw, min(30vw, 42vh));
          --cardh: var(--am-cardh, calc(var(--cardw) * 1.4));
        }
        .am-scrim {
          position: absolute; inset: 0; width: 100%; height: 100%;
          border: 0; padding: 0; background: transparent; cursor: zoom-out;
          animation: amFade .3s ease both;
        }
        @keyframes amFade { from { opacity: 0 } }

        .am-card {
          position: absolute; left: 50%; top: 50%;
          margin: 0; width: var(--cardw); height: var(--cardh);
          transform: translate(-50%, -50%);
          border-radius: 10px; overflow: hidden;
          box-shadow: 0 40px 90px rgba(0,0,0,.7), 0 0 0 1px rgba(255,255,255,.07);
          /* The chrome CROSS-FADES IN as the frame lands — it does not travel
             with it. Type sliding and scaling across the screen for half a
             second is unreadable, so the photograph makes the journey and the
             words simply arrive once it has stopped. */
          animation: amChrome .3s ease .28s both;
          pointer-events: none;
        }
        @keyframes amChrome { from { opacity: 0 } }
        @keyframes amCard {
          from { opacity: 0; transform: translate(-50%,-46%) scale(.94); }
        }
        /* the caption sits ON the photograph, so it needs its own ground */
        .am-card figcaption {
          position: absolute; left: 0; right: 0; bottom: 0;
          padding: 34px 16px 14px;
          background: linear-gradient(to top, rgba(6,6,8,.86), rgba(6,6,8,0));
        }
        .am-card h3 {
          margin: 0 0 8px; display: flex; align-items: center; gap: 6px;
          font-family: var(--font-instrument-serif), Georgia, serif;
          font-weight: 400; font-size: clamp(17px, 1.5vw, 24px); line-height: 1;
        }
        .am-card h3 svg { width: 13px; height: 13px; flex: none; }
        .am-card h3 svg circle { fill: #4ea3ff; }
        .am-card h3 svg path { fill: none; stroke: #fff; stroke-width: 1.7; }
        .am-card ul {
          display: flex; flex-wrap: wrap; gap: 5px; margin: 0; padding: 0;
          list-style: none;
        }
        .am-card li {
          padding: 3px 8px; border-radius: 999px;
          background: rgba(255,255,255,.13);
          font-size: 9px; letter-spacing: .04em;
        }
        .am-seg {
          position: absolute; top: 12px; right: 12px; display: flex; gap: 3px;
        }
        .am-seg b { width: 12px; height: 2px; border-radius: 2px; background: rgba(255,255,255,.34); }
        .am-seg b:first-child { background: #fff; width: 18px; }
        .am-acts {
          position: absolute; right: 12px; bottom: 14px;
          display: flex; flex-direction: column; gap: 8px;
        }
        .am-acts b {
          width: 26px; height: 26px; border-radius: 50%;
          display: grid; place-items: center; font-weight: 400; font-size: 11px;
          background: rgba(255,255,255,.16); backdrop-filter: blur(6px);
        }

        .am-collect, .am-rail, .am-hint { position: absolute; z-index: 2; }
        .am-collect {
          left: 50%; top: calc(50% - var(--cardh) / 2 - 46px);
          transform: translateX(-50%);
          display: inline-flex; align-items: center; gap: 7px;
          padding: 8px 15px; border-radius: 999px;
          border: 1px solid rgba(244,241,234,.2);
          background: rgba(20,20,24,.7); backdrop-filter: blur(10px);
          color: inherit; font-size: 11px; cursor: pointer;
          animation: amCard .42s cubic-bezier(.16,1,.3,1) .34s both;
        }
        .am-collect i { font-style: normal; opacity: .7; }
        .am-rail {
          left: calc(50% - var(--cardw) / 2 - 52px); top: 50%;
          transform: translateY(-50%);
          display: flex; flex-direction: column; gap: 6px; padding: 7px 6px;
          border-radius: 999px; background: rgba(20,20,24,.6);
          border: 1px solid rgba(244,241,234,.12);
          animation: amCard .42s cubic-bezier(.16,1,.3,1) .4s both;
        }
        .am-railbtn {
          width: 24px; height: 24px; border-radius: 50%;
          display: grid; place-items: center; font-size: 12px; opacity: .75;
        }
        .am-hint {
          left: 0; right: 0; bottom: 5vh; margin: 0; text-align: center;
          font-size: 10.5px; letter-spacing: .04em; opacity: .5;
          animation: amCard .42s cubic-bezier(.16,1,.3,1) .46s both;
        }
        .am-hint kbd {
          display: inline-grid; place-items: center;
          min-width: 17px; height: 17px; padding: 0 4px; margin: 0 1px;
          border-radius: 4px; border: 1px solid rgba(244,241,234,.24);
          font: inherit; font-size: 9.5px;
        }

        /* On a phone the card takes the width and the side rail has nowhere
           to go, so it moves under the collect pill rather than off-screen. */
        /* ── the rail, MOVED UNDER THE CARD ───────────────────────────────
           Same reasoning as the stacked copy: the question is whether there is
           a column left beside the card, which only the canvas can answer
           because the canvas is what sized the card. On the aspect query it
           used to answer wrong on a portrait tablet — the card was two-thirds
           of 768 and the rail, sent below by the query, came down across its
           own bottom edge. */
        .am-root[data-rail="under"] .am-collect {
          top: calc(50% - var(--cardh) / 2 - 44px);
        }
        .am-root[data-rail="under"] .am-rail {
          left: 50%; top: auto; bottom: 8vh;
          transform: translateX(-50%); flex-direction: row;
        }

        /* Keyed to ASPECT, the same test the canvas uses to pick its solve —
           a width query would put the phone chrome on a landscape tablet.
           What is LEFT in here is only what is genuinely about the shape of the
           screen: the bar's columns, and a hint about keys on a screen that
           has none. Everything that depends on how big the heart or the card
           came out has moved to the data attributes above, where it can be
           measured instead of guessed. */
        @media (max-aspect-ratio: 9/10) {
          /* no centre column on a phone: the wordmark takes the left edge */
          .am-bar { grid-template-columns: 1fr; padding: 2.4vh 5vw; }
          .am-mark { grid-column: 1; }
          .am-hint { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .am-root > *:not(.am-cv) { transition-duration: .01s !important; transition-delay: 0s !important; }
        }
      `}</style>
    </div>
  );
}
